import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  ItemEvent,
  ItemEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/item_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { ItemCreatedDetail } from './ItemCreatedDetail';
import { ItemDeletedDetail } from './ItemDeletedDetail';
import { ItemUpdatedDetail } from './ItemUpdatedDetail';

import { useTranslations } from '@/inventory/i18n/useTranslations';

type ItemEventDetailProps = {
  payload: string;
};

export const ItemEventDetail = ({ payload }: ItemEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): ItemEvent | null => {
    try {
      return fromJson(ItemEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <ItemCreatedDetail event={inner.value} />;
      case 'updated':
        return <ItemUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <ItemDeletedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.itemCreated;
      case 'updated':
        return t.logs.events.itemUpdated;
      case 'deleted':
        return t.logs.events.itemDeleted;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@ItemEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
